import Link from 'next/link'
import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import ReactCodeInput from 'react-code-input'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { useWeb3React } from '@web3-react/core'
import { getCodeDetail, postInvitationCode, signMessage } from '~/apis'
import { Loading } from '~/components/Loading'
import { message } from '~/components/Message'
import { useBreakpoint, useGA } from '~/hooks'
import { detectImTokenApp } from '~/connection/detect'
import { goTokenlon } from '~/utils/userAgent'
import { StyledActivateModal } from './styled'
import { ASSET_PREFIX } from '~/constants/env'

interface Props {
  loading: boolean
  boundCode: string
  activated: boolean
  setActivated: (activated) => void
}

export const ActivateContent: React.FC<Props> = ({
  loading,
  boundCode,
  activated,
  setActivated,
}) => {
  const { isMobile } = useBreakpoint()
  const { t } = useTranslation()
  const { trackClick } = useGA()
  const router = useRouter()

  const [code, setCode] = useState<string>()
  const [activating, setActivating] = useState(false)

  const { account: address, provider } = useWeb3React()
  const connected = !!address

  useEffect(() => {
    const { r: urlCode } = router.query
    if (urlCode && typeof urlCode === 'string' && urlCode.length === 5) {
      setCode(urlCode)
    }
  }, [router.query])

  const { data: codeDetail, error, isValidating } = useSWR(
    ['getCodeDetail', code],
    () => getCodeDetail(code),
  )
  const isCodeValid = !error && code && code.length === 5 && !isValidating

  const onSign = async () => {
    setActivating(true)
    try {
      const inviter = codeDetail?.address
      const invitee = address
      if (inviter === invitee) {
        return message.error(t('not_allow_activate_code_address'))
      }
      const timestamp = Date.now()
      const result = await signMessage(provider, `${inviter}&${timestamp}`)
      await postInvitationCode(code, {
        inviter,
        invitee,
        signature: result,
        timestamp,
      })
      setActivated(true)
      trackClick('webtl_trademining_bind_confirm', {
        title: code,
        is_success: 1,
      })
      message.success(t('success_activate'))
    } catch (error) {
      const _message =
        error.response && error.response.data
          ? `: ${error.response.data.message}`
          : ''
      message.error(`${t('error_activate')}${_message}`)
      trackClick('webtl_trademining_bind_confirm', {
        title: code,
        is_success: 0,
        message: _message,
      })
    }
    setActivating(false)
  }

  if (loading && connected) {
    return <Loading />
  }

  if (!!boundCode) {
    return (
      <div className="activate-result">
        <img
          src={`${ASSET_PREFIX}/images/lon/bound-code.svg`}
          alt="activate success"
        />
        <p className="title">{t('invitation_ENABLED')}</p>
        <p className="subtitle">{`${t('invitation_code')}: ${boundCode}`}</p>
      </div>
    )
  }

  if (activated) {
    return (
      <div className="activate-result">
        <img
          src={`${ASSET_PREFIX}/images/activate-success.svg`}
          alt="activate success"
        />
        <p className="title">{t('activate_success')}</p>
        <p className="subtitle">{t('activate_success_subtitle')}</p>
        {detectImTokenApp ? (
          <Link href="/instant">
            <button>{t('forward_to_exchange')}</button>
          </Link>
        ) : (
          <button onClick={() => goTokenlon(address)}>
            {t('forward_to_exchange')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div>
      <p className="label">{t('lon_invitation_code')}</p>
      <div className="code-inputs-wrap">
        <ReactCodeInput
          key={`code-input-${code}`}
          name="code-input"
          inputMode="latin"
          type="text"
          fields={5}
          value={code}
          forceUppercase
          className="code-inputs"
          onChange={(_code) => setCode(_code.toUpperCase())}
        />
      </div>
      <p className="label">{t('my_wallet_address')}</p>
      <div className="wallet-address-wrap">
        <p>{address || ''}</p>
      </div>
      <div
        className="activate-button-wrap"
        style={{ paddingBottom: isMobile ? 40 : 10 }}
      >
        <button
          className={isCodeValid && connected ? 'active' : ''}
          disabled={!connected || !isCodeValid}
          onClick={onSign}
        >
          {t('activate')}
        </button>
      </div>
      {activating && (
        <StyledActivateModal>
          <Loading />
        </StyledActivateModal>
      )}
    </div>
  )
}
